import { LoadingSpinner } from "components/LoadingSpinner";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import { useGetPackageRulesetByIdQuery } from "store/queries/packageRuleset";

import { BundledCptCodes } from "./components/BundledCptCodes";
import { ContractLanguageField } from "./components/ContractLanguageField";
import { Header } from "./components/Header";
import { PackageCalculationExamples } from "./components/PackageCalculationExamples";
import { ProviderAndPayerCopySection } from "./components/ProviderAndPayerCopySection";
import { RuleName } from "./components/RuleName";
import { SubmitPackages } from "./components/SubmitPackages";
import { TriggerCptCodes } from "./components/TriggerCptCodes";
import { copyFormScope } from "./helpers/ruleApplicability";

export function CopyPackagesPage() {
  useDocumentTitle("Copy Package Rules");
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const asDefault = searchParams.get("asDefault") === "true";
  const { data: ruleset, isLoading } = useGetPackageRulesetByIdQuery(id);

  const methods = useForm({
    defaultValues: {
      // CLE-2842 step 2 — applicability, the source's provider and payer (All payers on a
      // Payer-Provider rule that had none), and a Fallback's payer.
      ...copyFormScope(ruleset?.data, asDefault),
      groupName: ruleset?.data.groupName,
      triggerCptCodes: ruleset?.data.triggerCptCodes,
      bundledCptCodes: ruleset?.data.bundledCptCodes,
      contractLanguage: ruleset?.data.contractLanguage ?? "",
    },
  });

  useEffect(() => {
    if (ruleset) {
      methods.reset({
        ...copyFormScope(ruleset?.data, asDefault),
        groupName: ruleset?.data.groupName,
        triggerCptCodes: ruleset?.data.triggerCptCodes,
        bundledCptCodes: ruleset?.data.bundledCptCodes,
      });
    }
  }, [ruleset, methods, asDefault]);

  const copyScope = copyFormScope(ruleset?.data, asDefault);

  if (isLoading) {
    return <LoadingSpinner />;
  }
  return (
    <FormProvider {...methods}>
      <Header name={ruleset?.data.groupName} isPackages />
      <ProviderAndPayerCopySection
        initialProviders={copyScope.provider}
        initialPayers={copyScope.payerName}
      />
      <RuleName />
      <TriggerCptCodes />
      <BundledCptCodes />
      <PackageCalculationExamples />
      <ContractLanguageField ruleType="PACKAGE" />
      {id && ruleset && <SubmitPackages />}
    </FormProvider>
  );
}
